"use client";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { DocumentNode, print } from "graphql";
import { useCallback, useEffect, useState } from "react";

import { fetcher } from "../services/api";
import config from "../services/config";

interface FetcherParams {
  variables?: any;
  authenticated?: boolean;
}

export interface FetcherError {
  text: string;
  status?: number;
  messages?: string[];
  codes?: string[];
}

export type FetcherResult<T> = { data?: T; error?: FetcherError; };
export type HookResult<T> = FetcherResult<T> & { loading: boolean; };

const API_URL = `${config.apiBaseHost}/iv-website-gw/graphql`;

const r = (query: DocumentNode, params?: FetcherParams) => fetcher(API_URL, {
  cache: "no-cache",
  method: "POST",
  body: JSON.stringify({
    query: print(query),
    variables: params?.variables,
  }),
}, params.authenticated || false);

export const useLazyFetcher = <T = any>(query: DocumentNode, params?: FetcherParams): [
  (params?: FetcherParams) => Promise<FetcherResult<T> | null>,
  HookResult<T>,
] => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<FetcherError>(null);
  const [data, setData] = useState<T>(null);

  const f = useCallback(async (parameters: FetcherParams = {}) => {
    setLoading(true);
    const res = await r(query, { ...params, ...parameters });
    if (!res) {
      setError({ text: "fetchError.emptyResult" });
      setLoading(false);
      throw new Error("fetchError.emptyResult");
    }

    setData(res?.data || null);
    setError(res?.error || null);
    setLoading(false);
    return res || null;
  }, [params]);

  return [f, { loading, error, data }];
};

export const useFetcher = <T = any>(query: DocumentNode, params?: FetcherParams): [
  () => Promise<FetcherResult<T> | null>,
  HookResult<T>,
] => {
  const [f, {
    loading, error, data,
  }] = useLazyFetcher<T>(query, params);
  useEffect(() => {
    f(params);
  }, []);

  return [f, { loading, error, data }];
};
